import { CompareChangeType } from "src/components/CompareChangesModal/types";
import { formatDurationLabel } from "src/components/CompareChangesModal/utils/formatDurationLabel";
import { toLastEditedBy } from "src/components/CompareChangesModal/utils/toLastEditedBy";
import { isHidden } from "src/helpers/channelHelper";
import { Content } from "src/pages/Channels/ChannelDetail/types";
import {
  ChannelDiff,
  DiffEntry,
  compareChannel,
} from "src/utils/publishedDraftDiff";
import {
  ChannelEntity,
  ChannelEntityOptions,
  ChannelListItem,
  DefaultDurations,
  buildChannelEntityIndex,
  getChannelEffectiveDurationMs,
} from "./channelCompareEntities";
import {
  ChannelCompareChanges,
  ChannelCompareContentItem,
  ChannelCompareInput,
  ChannelCompareZoneState,
} from "./channelCompareTypes";
import {
  getChannelCompareZones,
  resolveSelectedZone,
} from "./channelCompareZones";

/**
 * Maps a channel's published-vs-draft state for one zone into the Compare
 * Changes modal data (GRWBKK-2039), mirroring preparePlaylistCompareChanges.
 * Pure: all inputs come from the channelCompareChangesById query.
 *
 * Only row changes are shown and counted. Layout, zone sizing / transition,
 * hidden-zone and channel dimension changes are ignored; a default_durations
 * change surfaces through the rows whose effective duration it changes.
 */

export type PrepareChannelCompareChangesOptions = ChannelEntityOptions;

interface NormalizedZone {
  list: ChannelListItem[];
  props: unknown;
}

interface NormalizedContent {
  props: { default_durations?: DefaultDurations };
  zones: Record<string, NormalizedZone>;
}

interface RawContent {
  props?: { default_durations?: DefaultDurations } | null;
  zones?: Record<string, { list?: unknown[] | null; props?: unknown } | null>;
}

interface ZoneContext {
  published: NormalizedContent;
  draft: NormalizedContent;
  diff: ChannelDiff;
  entityIndex: Map<string, ChannelEntity>;
  options: PrepareChannelCompareChangesOptions;
}

interface PreparedZone {
  liveItems: ChannelCompareContentItem[];
  changesItems: ChannelCompareContentItem[];
  totalChanges: number;
}

const isListItem = (item: unknown): item is ChannelListItem =>
  !!item &&
  typeof (item as ChannelListItem).list_id === "string" &&
  !!(item as ChannelListItem).content;

/**
 * Makes raw content safe to diff: a missing side becomes an empty channel,
 * the hidden (background audio) zone is dropped, and template markers or
 * null rows are filtered out of each list.
 */
const normalizeContent = (
  content: RawContent | null | undefined,
): NormalizedContent => {
  const zones: Record<string, NormalizedZone> = {};
  Object.entries(content?.zones ?? {}).forEach(([key, zone]) => {
    if (isHidden(key)) {
      return;
    }
    zones[key] = {
      list: (zone?.list ?? []).filter(isListItem),
      props: zone?.props ?? {},
    };
  });
  return { props: content?.props ?? {}, zones };
};

const getDefaults = (content: NormalizedContent): DefaultDurations =>
  content.props.default_durations ?? {};

const getEntity = (
  item: ChannelListItem,
  entityIndex: Map<string, ChannelEntity>,
): ChannelEntity | undefined => {
  const refId = item.content._ref?.id;
  return refId ? entityIndex.get(refId) : undefined;
};

interface ResolveRowOptions {
  item: ChannelListItem;
  position: number;
  defaults: DefaultDurations;
  context: ZoneContext;
}

const resolveRow = ({
  item,
  position,
  defaults,
  context,
}: ResolveRowOptions): ChannelCompareContentItem => {
  const ref = item.content._ref;
  const entity = getEntity(item, context.entityIndex);
  const base = {
    id: item.list_id,
    position,
    schedule: (item.rules ?? []) as ChannelCompareContentItem["schedule"],
  };
  if (!ref || !entity) {
    return {
      ...base,
      title: context.options.intl.formatMessage({
        id: "compare_changes.unknown_item",
        defaultMessage: "Unknown item",
      }),
    };
  }
  return {
    ...base,
    title: entity.title,
    subtitle: entity.subtitle || undefined,
    thumbnailUrl: entity.thumbnailUrl,
    isAppIcon: entity.isAppIcon,
    isPlaylist: entity.isPlaylist,
    thumbnailColor: entity.thumbnailColor,
    durationLabel: entity.isPlaylist
      ? undefined
      : formatDurationLabel(
          getChannelEffectiveDurationMs(item, entity, defaults),
        ),
    contentRef: { type: ref.type, id: ref.id, mimetype: entity.mimetype },
  };
};

const CHANGE_TYPE_BY_OP: Record<
  DiffEntry<ChannelListItem>["op"],
  CompareChangeType
> = {
  equal: "unchanged",
  insert: "added",
  delete: "removed",
  move: "reordered",
  update: "edited",
};

const toChangesItem = (
  entry: DiffEntry<ChannelListItem>,
  position: number,
  context: ZoneContext,
): ChannelCompareContentItem => {
  const item = entry.op === "update" ? entry.draft : entry.item;
  const entity = getEntity(item, context.entityIndex);
  const publishedDefaults = getDefaults(context.published);
  const draftDefaults = getDefaults(context.draft);
  // A removed row keeps its live duration; every other row shows the draft's.
  const base = resolveRow({
    item,
    position,
    defaults: entry.op === "delete" ? publishedDefaults : draftDefaults,
    context,
  });

  // A default_durations change edits the effective duration of rows that
  // inherit it even though their own props are untouched (GRWBKK-1773). Rows
  // with their own, fixed or intrinsic duration are unaffected.
  const isDerivedEdited =
    (entry.op === "equal" || entry.op === "move") &&
    !!entity &&
    getChannelEffectiveDurationMs(item, entity, publishedDefaults) !==
      getChannelEffectiveDurationMs(item, entity, draftDefaults);

  const changeType: CompareChangeType = isDerivedEdited
    ? "edited"
    : CHANGE_TYPE_BY_OP[entry.op];

  const moved =
    (entry.op === "update" && entry.moved) ||
    (entry.op === "move" && isDerivedEdited)
      ? { movedFrom: entry.fromIndex + 1, movedTo: entry.toIndex + 1 }
      : undefined;

  return { ...base, changeType, ...moved };
};

const prepareZone = (
  key: string,
  state: ChannelCompareZoneState,
  context: ZoneContext,
): PreparedZone => {
  const { published, draft, diff } = context;
  const toRows = (list: ChannelListItem[], defaults: DefaultDurations) =>
    list.map((item, i) =>
      resolveRow({ item, position: i + 1, defaults, context }),
    );

  if (state === "added") {
    // The zone did not exist before: every draft row is new, nothing is live.
    const changesItems = toRows(draft.zones[key].list, getDefaults(draft)).map(
      (row): ChannelCompareContentItem => ({ ...row, changeType: "added" }),
    );
    return { liveItems: [], changesItems, totalChanges: changesItems.length };
  }

  const liveItems = toRows(published.zones[key].list, getDefaults(published));
  if (state === "deleted") {
    // Publishing removes the zone and its rows. They stay unmarked on the live
    // side but count, so removing the zone alone still enables Publish.
    return { liveItems, changesItems: [], totalChanges: liveItems.length };
  }

  const changesItems = diff.zones[key].entries.map((entry, i) =>
    toChangesItem(entry as DiffEntry<ChannelListItem>, i + 1, context),
  );
  const totalChanges = changesItems.filter(
    (item) => item.changeType && item.changeType !== "unchanged",
  ).length;
  return { liveItems, changesItems, totalChanges };
};

export const prepareChannelCompareChanges = (
  channel: ChannelCompareInput,
  selectedZone: string | undefined,
  options: PrepareChannelCompareChangesOptions,
): ChannelCompareChanges => {
  const published = normalizeContent(channel.content);
  // Mirrors the playlist: a missing draft compares as an empty channel.
  const draft = normalizeContent(channel.draft?.content);
  // Layout ids are not passed: a layout change alone is not shown or counted.
  const diff = compareChannel(
    published as unknown as Content,
    draft as unknown as Content,
  );

  // Published first, draft second: draft entities win on conflict.
  const entityIndex = buildChannelEntityIndex(
    [channel, ...(channel.draft ? [channel.draft] : [])],
    options,
  );
  const context: ZoneContext = {
    published,
    draft,
    diff,
    entityIndex,
    options,
  };

  const zones = getChannelCompareZones(channel, diff);
  const preparedZones = new Map(
    zones.map((zone) => [zone.key, prepareZone(zone.key, zone.state, context)]),
  );
  const channelTotalChanges = Array.from(preparedZones.values()).reduce(
    (sum, zone) => sum + zone.totalChanges,
    0,
  );

  const activeZone = resolveSelectedZone(zones, selectedZone);
  const active = activeZone ? preparedZones.get(activeZone.key) : undefined;

  return {
    currentlyLive: {
      publishedAt: channel.publishedAt ?? undefined,
      items: active?.liveItems ?? [],
    },
    changes: {
      items: active?.changesItems ?? [],
      totalChanges: active?.totalChanges ?? 0,
      lastEditedBy: toLastEditedBy(channel.draft?.userByUpdatedBy),
    },
    channelTotalChanges,
    zones,
  };
};
